import { Alert, Button, Skeleton } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormGuardActions } from '@/entities/formGuard';
import { useIntegration } from '@/entities/integrations';
import { AnonymizerClassList } from './ui/AnonymizerClassList';
import { AnonymizerConfigCard } from './ui/AnonymizerConfigCard';
import { MasterDetailLayout, PageContainer } from '@/shared/ui';

// Код интеграции анонимизатора в реестре интеграций
const ANONYMIZER_INTEGRATION_CODE = 's1-anonymizer';

export const AnonymizerConfigPage: FC = () => {
  const { t } = useTranslation('anonymizerConfig');
  const { '*': classPath } = useParams();
  const navigate = useNavigate();
  const { setIsDirty } = useFormGuardActions();

  // Гейтинг страницы по статусу интеграции анонимизатора
  const { data: integration, isLoading: isIntegrationLoading } = useIntegration(ANONYMIZER_INTEGRATION_CODE);

  // Контент доступен только при включённой и настроенной интеграции с живым соединением
  const isIntegrationReady = useMemo(() => {
    if (!integration || !integration.isActive) return false;
    const requiredEnvFilled = (integration.envVariables ?? []).every((variable) => !variable.required || variable.isFilled);
    return requiredEnvFilled && Boolean(integration.connection?.available);
  }, [integration]);

  const handleBack = () => navigate('/anonymizer-config');
  const handleOpenIntegrations = () => navigate('/integrations/list');

  if (isIntegrationLoading) {
    return (
      <PageContainer
        enableScroll={false}
        outerClassName="h-full"
        className="flex flex-col h-full gap-4 w-full"
      >
        <h1 className="text-2xl font-semibold">{t('title')}</h1>
        <Skeleton className="h-full" />
      </PageContainer>
    );
  }

  // Пустое состояние: интеграция выключена либо не настроена
  if (!isIntegrationReady) {
    return (
      <PageContainer
        enableScroll={false}
        outerClassName="h-full"
        className="flex flex-col h-full gap-4 w-full"
      >
        <h1 className="text-2xl font-semibold">{t('title')}</h1>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <Alert
            type="info"
            className="max-w-xl text-left"
            title={t('integrationGate.title')}
            description={t('integrationGate.description')}
          />
          <Button variant="secondary" onClick={handleOpenIntegrations}>
            {t('integrationGate.openIntegrations')}
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <MasterDetailLayout
      isDetailOpen={Boolean(classPath)}
      onBack={handleBack}
      toolbar={<h1 className="text-2xl font-semibold">{t('title')}</h1>}
      listClassName="@2xl:w-64"
      detailClassName="max-w-4xl"
      list={<AnonymizerClassList activeClass={classPath} />}
      detail={
        <AnonymizerConfigCard className={classPath} key={classPath} onUnsavedChange={(dirty) => setIsDirty(dirty)} />
      }
    />
  );
};
